'use client'

import * as React from 'react'
import { useIntersectionObserver } from 'hooks/use-intersection-observer'
import { ProductCard } from '@web/components/product-card/product-card'
import type { Locales } from 'cf-i18n/helpers'
import { addOrReplaceLocaleInUrl } from 'cf-i18n/helpers'
import type {
  Language,
  LocalizedStrings,
  ClassProperties,
  DesignerProfile as TDesignerProfile,
} from 'codegen/types'
import { GetCategoryProductsQueryFetcher } from '@web/gen/category'
import * as Sentry from '@sentry/nextjs'

interface Product {
  productId?: number | null
  productUrl?: string | null
  productImage?: string | null
  imageAlt?: string | null
  productTitle?: string | null
  categoryUrl?: string | null
  productTitleLocalized?: LocalizedStrings | null
  cardCategory?: LocalizedStrings | null
  productCountdown?: string | null
  designer?: TDesignerProfile | null
  classProperties?: ClassProperties | null
}

interface InfiniteProductLoaderProps {
  locale: Locales
  slug: string
  orderBy: string
  limit: number
  hidePriceSection?: boolean
}

export function CategoryInfiniteProductLoader({
  locale,
  slug,
  orderBy,
  limit,
  hidePriceSection = false,
}: InfiniteProductLoaderProps) {
  const [products, setProducts] = React.useState<Product[]>([])
  const [isLoading, setIsLoading] = React.useState(false)
  const [hasMore, setHasMore] = React.useState(true)
  const [isInitialized, setIsInitialized] = React.useState(false)
  const hasTriggeredInThisIntersection = React.useRef(false)
  const pageRef = React.useRef(1)

  const [ref, entry] = useIntersectionObserver<HTMLDivElement>()
  const isIntersecting = entry?.isIntersecting ?? false

  const fetchProducts = React.useCallback(
    async (pageNum: number) => {
      const fetcher = GetCategoryProductsQueryFetcher({
        input: {
          slug,
          orderBy,
          limit,
          page: pageNum,
        },
      })
      const data = await fetcher()
      return data
    },
    [slug, orderBy, limit],
  )

  // Initialize from URL on mount (runs only once)
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const pageParam = urlParams.get('page')

    if (pageParam !== null) {
      const urlPage = parseInt(pageParam, 10)

      // If page param is not a valid number or is 1, remove it from URL
      if (isNaN(urlPage) || urlPage <= 1) {
        const url = new URL(window.location.href)
        url.searchParams.delete('page')
        window.history.replaceState({}, '', url.toString())
        setIsInitialized(true)
        return
      }

      // Set the page number and fetch that page's content
      pageRef.current = urlPage
      setIsLoading(true)

      GetCategoryProductsQueryFetcher({
        input: {
          slug,
          orderBy,
          limit,
          page: urlPage,
        },
      })()
        .then(data => {
          const pageProducts = data?.getCategoryProducts?.products || []
          if (pageProducts.length) {
            setProducts(pageProducts)
          }
          const totalItems = data?.getCategoryProducts?.pagination?.totalItems || 0
          if (urlPage * limit >= totalItems) {
            setHasMore(false)
          }
          setIsLoading(false)
          setIsInitialized(true)
        })
        .catch(error => {
          Sentry.captureException('Error fetching initial page:', error)
          setIsLoading(false)
          setIsInitialized(true)
        })
      return
    }

    setIsInitialized(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const loadMore = React.useCallback(async () => {
    const nextPage = pageRef.current + 1
    setIsLoading(true)
    pageRef.current = nextPage

    // Update URL with pushState first
    const url = new URL(window.location.href)
    url.searchParams.set('page', String(nextPage))
    window.history.pushState({}, '', url.toString())

    try {
      const data = await fetchProducts(nextPage)
      const pageProducts = data?.getCategoryProducts?.products || []

      if (pageProducts.length) {
        setProducts(prev => [...prev, ...pageProducts])
      }

      const totalItems = data?.getCategoryProducts?.pagination?.totalItems || 0
      if (nextPage * limit >= totalItems) {
        setHasMore(false)
      }
    } catch (error) {
      Sentry.captureException(error)
    } finally {
      setIsLoading(false)
    }
  }, [fetchProducts, limit])

  // Reset trigger flag when intersection changes
  React.useEffect(() => {
    if (!isIntersecting) {
      hasTriggeredInThisIntersection.current = false
    }
  }, [isIntersecting])

  // Handle intersection for loading more
  React.useEffect(() => {
    if (
      isInitialized &&
      isIntersecting &&
      !hasTriggeredInThisIntersection.current &&
      hasMore &&
      !isLoading
    ) {
      hasTriggeredInThisIntersection.current = true
      loadMore()
    }
  }, [isIntersecting, isInitialized, hasMore, isLoading, loadMore])

  if (products.length === 0 && !isLoading) {
    return <div ref={ref} className="h-px w-full bg-transparent" />
  }

  return (
    <>
      {products.map(product => (
        <ProductCard
          key={product.productId}
          productId={String(product.productId || 0)}
          productUrl={addOrReplaceLocaleInUrl(product.productUrl || '', locale as Language)}
          imageClassName="aspect-[3/2] object-cover"
          image={product.productImage || ''}
          name={product.productTitleLocalized?.[locale] || product.productTitle || ''}
          alt={product.imageAlt || ''}
          category={product.cardCategory?.[locale] || ''}
          categoryUrl={
            product.categoryUrl
              ? addOrReplaceLocaleInUrl(product.categoryUrl || '', locale as Language)
              : undefined
          }
          countdown={
            product.productCountdown
              ? new Date(product.productCountdown.replace(/\//g, '-')).getTime()
              : undefined
          }
          extraSettings={
            (product.classProperties as {
              level?: string
              source?: string
            }) || undefined
          }
          hidePriceSection={hidePriceSection}
          isLegacy
        />
      ))}
      <div ref={ref} className="h-px w-full bg-transparent" />
    </>
  )
}
